import { FC, useEffect } from 'react';
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';

import PlanCard from '../PlanCard';
import { Values } from '../../pages/PersonalizedForm/PersonalizedForm';

import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { setMembers, setSelectedPlan, setSelectedPlanId } from '../../store/slices/planSlice';
import { useSubscriptionCheckoutMutation, useUpdatePlanMutation } from '../../store/services/customizeOptionsApi';

import styles from './Plans.module.scss';
import PremiumPlanCard from '../PlanCard/PremiumPlanCard';

type PlanAdvantage = {
  id: number;
  content: string;
  measure: boolean | string;
};

export type ReferralDiscount = {
  is_eligible: boolean;
  discount_percent: number;
  original_price_in_cents: number;
  discount_in_cents: number;
  final_price_in_cents: number;
  reason: string;
};

export interface IPlan {
  id: number;
  group: 'Single';
  type: 'Basic' | 'Premium';
  price_in_cents: number;
  paddle_id: string;
  benefits: string[];
  advantages: PlanAdvantage[];
  ends_at: string | null;
  referral_discount?: ReferralDiscount;
}

type PlansProps = {
  values: Values | { plan: number };
  hideHeader?: boolean;
  onChangePlan: <T = string | React.ChangeEvent<any>>(
    field: T
  ) => T extends React.ChangeEvent<any> ? void : (e: string | React.ChangeEvent<any>) => void;
  subscription?: boolean;
  onPlanAction?: (id: number) => void;
};

const Plans: FC<PlansProps> = ({ values, hideHeader = false, onChangePlan, subscription, onPlanAction }) => {
  const dispatch = useAppDispatch();
  const plans = useAppSelector(state => state.plan.plans);
  const [updatePlanMutation] = useUpdatePlanMutation();
  const [subscriptionCheckout] = useSubscriptionCheckoutMutation();
  const user = useAppSelector(state => state.user);

  useEffect(() => {
    return () => {
      dispatch(setMembers(3));
    };
  }, [dispatch]);

  useEffect(() => {
    if (subscription || !plans?.length) return;

    const defaultPlan =
      plans.find((plan: IPlan) => plan.type === 'Premium') ??
      plans.find((plan: IPlan) => plan.type === 'Basic');

    if (defaultPlan) {
      dispatch(setSelectedPlan(defaultPlan));
    }
  }, [plans, subscription, dispatch]);

  const handlePlanClick = async (id: number) => {
    const selectedPlan = plans?.find((plan: IPlan) => plan.id === id);

    try {
      if (!user.is_premium && selectedPlan?.type === 'Premium') {
        const data = await subscriptionCheckout({ plan_id: id }).unwrap();
        if (data.checkout_url) {
          window.location.assign(data.checkout_url);
        }
        return;
      } else {
        await updatePlanMutation({ plan_id: id });
      }
      dispatch(setSelectedPlanId({ id }));
    } catch (error) {
      console.error(error);
    }
  };

  const handleSelectedPlan = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedPlan = plans && plans.find((plan: IPlan) => plan.id === Number(e.target.value));

    if (!selectedPlan) return;
    onChangePlan(e);
    dispatch(setSelectedPlan(selectedPlan));
  };

  const visiblePlans = plans ? plans.filter((plan: IPlan) => plan.type === 'Premium') : [];
  const hasReferralDiscount = plans?.some(
    (plan: IPlan) => plan.referral_discount?.is_eligible && plan.price_in_cents > 0,
  );

  return (
    <Box className={styles.plansRoot}>
      <Box
        gap={3}
        className={`${styles.header} ${hideHeader ? styles.hideHeader : ''}`}
      >
        <Box
          display="flex"
          flexDirection="column"
        >
          <Typography
            variant="h5Bold"
            sx={{ textAlign: 'center', color: '#222B3A' }}
          >
            Find the right plan for you
          </Typography>
          <Typography
            variant="h10"
            sx={{ textAlign: 'center', color: '#5E6A81', '& .textBlack': { color: '#000000' } }}
          >
            Upgrade to experience the full power of <span className="textBlack">TravGenius</span>
          </Typography>
        </Box>
        <Typography
          variant="h10"
          sx={{ textAlign: 'center', color: hasReferralDiscount ? '#208B64' : '#222B3A', fontSize: '18px' }}
        >
          {hasReferralDiscount ? 'Referral discount applied to Premium!' : 'Limited time pricing!'}
        </Typography>
      </Box>

      <FormControl
        component="fieldset"
        className={styles.radioGroupWrapper}
      >
        <RadioGroup
          name="plan"
          className={styles.radioGroup}
          value={values.plan}
          onChange={handleSelectedPlan}
        >
          {subscription &&
            user.is_premium &&
            visiblePlans.map((plan: IPlan) => (
              <PremiumPlanCard
                key={plan.id}
                plan={plan}
              />
            ))}
          {!user.is_premium &&
            plans &&
            plans.map((plan: IPlan) => (
              <FormControlLabel
                key={plan.id}
                value={plan.id}
                disabled={user.is_premium && plan.id !== user.chosen_plan}
                control={<Radio sx={{ display: 'none' }} />}
                className={subscription ? styles.formLabel : ''}
                label={
                  <PlanCard
                    plan={plan}
                    selected={Number(values.plan) === plan.id}
                    disabled={user.is_premium && plan.id !== user.chosen_plan}
                    onClick={() => {
                      (onPlanAction ?? handlePlanClick)(Number(plan.id));
                    }}
                    subscription={Boolean(subscription)}
                  />
                }
              />
            ))}
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

export default Plans;
